/**
 * Punctul de intrare. Patru decizii aici:
 *
 *  1. `staleTime` de 30s si `refetchOnWindowFocus: false` — cifrele de trezorerie NU au voie sa se
 *     schimbe sub cursor doar pentru ca cineva a dat alt-tab. Reincarcarea e explicita (butonul
 *     `Reincarca` din bannerul de vechime), iar vechimea e mereu scrisa cu ora.
 *  2. `retry` doar pe erorile de retea si pe 5xx, prin `reincearca` din `lib/api-errors.ts`. Un 409
 *     (concurenta optimista pe `version`), un 422 sau un 401 nu se rezolva prin repetare, iar
 *     reincercarea unei mutatii ar putea dubla o plata. Politica sta intr-un singur loc tocmai ca sa
 *     nu ajunga sa fie scrisa a doua oara, altfel, langa vreun hook.
 *  3. Mutatiile NU se reiau automat la eroare (`retry: 0`), dar SE PAUZEAZA cand nu e retea:
 *     `networkMode` implicit le pune in asteptare, iar `pornesteCoadaOffline` le scrie in IndexedDB
 *     si le reia la reconectare. Un VPN cazut nu costa o dimineata de munca.
 *  4. `StrictMode` pornit: efectele duble in dezvoltare scot la iveala exact bug-urile de focus si
 *     de abonare din grila.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { router } from './router.tsx';
import { esteSesiuneExpirata, reincearca } from './lib/api-errors.ts';
import { chei, esteCheiaEu } from './lib/query-keys.ts';
import { pornesteCoadaOffline } from './hooks/offline.ts';
import './styles/app.css';

/**
 * Un 401 de ORIUNDE readuce ecranul de autentificare.
 *
 * Fara asta, fiecare ecran ar trebui sa trateze singur sesiunea expirata — si ar fi de ajuns ca
 * unul sa uite, ca sa ramana afisate cifre vechi peste o sesiune moarta. Aici se invalideaza cheia
 * `eu`, `Shell` reciteste `/api/eu`, primeste 401 si comuta pe login. O singura cale.
 *
 * **`eu` insusi e EXCLUS, si asta nu e un detaliu.** El e interogarea care CONSTATA lipsa sesiunii:
 * daca 401-ul lui ar declansa invalidarea lui, s-ar reinteroga singur la infinit, iar ecranul de
 * login n-ar apuca sa se aseze — exact bucla observata in productie la primul deploy.
 */
function laEroareDeSesiune(cauza: unknown): void {
  if (!esteSesiuneExpirata(cauza)) return;
  void queryClient.invalidateQueries({ queryKey: chei.eu() });
}

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (cauza, interogare) => {
      if (esteCheiaEu(interogare.queryKey)) return;
      laEroareDeSesiune(cauza);
    },
  }),
  // Mutatiile nu se pot auto-invalida: niciuna nu are cheia `eu`. Un logout care esueaza cu 401
  // chiar TREBUIE sa reimprospateze `eu` — e cazul in care sesiunea murise deja.
  mutationCache: new MutationCache({ onError: laEroareDeSesiune }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: reincearca,
    },
    mutations: {
      // Mutatiile nu se reiau automat DUPA o eroare: coada persistata si butonul `Reincearca` sunt
      // explicite. Pauzarea pe lipsa de retea e altceva si rămâne activa (`networkMode` implicit).
      retry: 0,
    },
  },
});

// Coada offline se porneste inainte de prima randare: o mutatie ramasa din sesiunea trecuta trebuie
// sa fie in `MutationCache` pana sa apuce bannerul sa se deseneze, altfel contorul clipeste de la 0.
if (typeof indexedDB !== 'undefined') {
  const coada = pornesteCoadaOffline({ queryClient });
  void coada.gata;
}

const container = document.getElementById('root');
if (!container) throw new Error('Lipsește elementul #root din index.html');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
